<script lang="ts" setup>
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { RegisterSettingStore } from '../../RegisterSettingStore';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import InputExcelForm from '../inputExcelForm/InputExcelForm.vue';
import InputOrderExcelForm from '../inputExcelForm/InputOrderExcelForm.vue';
import { AlterableHeaderInfo } from '../../interfaces/LinkingInput';
import { OrderExcelInfoSaveMode } from '../../interfaces/LinkingInput';
import { RegisterOrderExcelInfos } from '@/coreDomainsModule/entities/InputOrderExcelInfo';
import { UNSELECTED_HEADER_VALUE } from '@/registerInputModule/constants';
const t = i18n.t;

const props = defineProps<{
  store: RegisterSettingStore;
}>();

const { state } = storeToRefs(props.store);
const selectedFieldId = ref<string>('');

const emits = defineEmits<{
  (e: 'on-click-next'): void;
  (e: 'on-click-back'): void;
}>();

const imagesRequire = require.context('@/assets/img', false, /^\.\/register-input-step-flow-2.*\.png$/);
const localedRegisterInputStepFlowImage = ref(imagesRequire('./register-input-step-flow-2.png'));

watch(
  () => i18n.locale,
  (newVal) => {
    localedRegisterInputStepFlowImage.value =
      newVal === 'en' ? imagesRequire('./register-input-step-flow-2__en.png') : imagesRequire('./register-input-step-flow-2.png');
  },
  {
    immediate: true,
  }
);

/**
 * 選択されたフィールドを更新する
 * @param header - フィールド
 * @param updateValue - 更新する値
 */
const updateSelectedField = (header: AlterableHeaderInfo, updateValue: string) => {
  // 選択された値を更新
  state.value.linkingInput.alternateHeaderInfos = state.value.linkingInput.alternateHeaderInfos.map((linkedHeader) => {
    if (linkedHeader.header === header.header) {
      // 選択された値が「未選択」の場合は空文字列にする
      return {
        ...linkedHeader,
        value: updateValue !== UNSELECTED_HEADER_VALUE ? updateValue : '',
      };
    }
    return linkedHeader;
  });
};

/**
 * 戻るボタン
 */
const onClickBackButton = () => {
  emits('on-click-back');
};

/**
 * 次へボタン
 */
const onClickNextButton = () => {
  emits('on-click-next');
};

const selectedFieldSuggestions = computed(() => {
  return state.value.uploadInput.headerCandidates.map((candidate) => candidate.value);
});

const savedOrderExcelInfos = computed(() => {
  return state.value.homeInput.inputExcelInfo?.Order ?? [];
});

/**
 * setter
 * @param updateValue - 更新する値
 */
const updateInlineHeaderName = (updateValue: string) => {
  state.value.linkingInput.inlinedYearMonthInfo.headerName = updateValue;
};

/**
 * setter
 * @param updateValue - 更新する値
 */
const updateInHeaderStartColumnNumber = (updateValue: string) => {
  state.value.linkingInput.inHeaderYearMonthInfo.startColumnNumber = {
    ...state.value.linkingInput.inHeaderYearMonthInfo.startColumnNumber,
    raw: updateValue,
  };
};

/**
 * setter
 * @param updateValue - 更新する値
 */
const updateInHeaderEndColumnNumber = (updateValue: string) => {
  state.value.linkingInput.inHeaderYearMonthInfo.endColumnNumber = {
    ...state.value.linkingInput.inHeaderYearMonthInfo.endColumnNumber,
    raw: updateValue,
  };
};

/**
 * setter
 * @param updateValue - 更新する値
 */
const updateInHeaderStart = (updateValue: string) => {
  state.value.linkingInput.inHeaderYearMonthInfo.start = {
    ...state.value.linkingInput.inHeaderYearMonthInfo.start,
    raw: updateValue,
  };
};

/**
 * setter
 * @param updateValue - 更新する値
 */
const updateInHeaderEnd = (updateValue: string) => {
  state.value.linkingInput.inHeaderYearMonthInfo.end = {
    ...state.value.linkingInput.inHeaderYearMonthInfo.end,
    raw: updateValue,
  };
};

/**
 * 発注読み込み設定を更新する
 * @param orderExcelInfos - 発注読み込み設定一覧
 */
const updateOrderExcelInfos = (orderExcelInfos: RegisterOrderExcelInfos) => {
  state.value.linkingInput.orderExcelInfos = orderExcelInfos;
};

/**
 * 発注読み込み設定の保存方法を更新する
 * @param saveMode - 保存方法
 */
const updateOrderExcelInfoSaveMode = (saveMode: OrderExcelInfoSaveMode) => {
  state.value.linkingInput.orderExcelInfoSaveMode = saveMode;

  if (saveMode === 'replace' && !state.value.linkingInput.orderExcelInfoReplaceTargetId) {
    state.value.linkingInput.orderExcelInfoReplaceTargetId = savedOrderExcelInfos.value[0]?.id ?? '';
  }
};

/**
 * 置き換える発注読み込み設定IDを更新する
 * @param event - 変更イベント
 */
const updateOrderExcelInfoReplaceTargetId = (event: Event) => {
  state.value.linkingInput.orderExcelInfoReplaceTargetId = (event.target as HTMLSelectElement).value;
};
</script>

<template>
  <div class="linking-input">
    <div class="linking-input__title">{{ t('取引紐付け：タイトル') }}</div>

    <img :src="localedRegisterInputStepFlowImage" class="linking-input__progress" />

    <div class="linking-input__sub-title sub-title">
      <span class="sub-title__prefix"> {{ t('取引紐付け：本文') }}</span>
      <span class="sub-title__suffix"> {{ t('取引紐付け：注釈') }}</span>
    </div>

    <div class="linking-input__linking-container">
      <div v-if="state.uploadInput.inputFileType === '発注'" class="linking-input__order-save-mode order-save-mode">
        <label class="order-save-mode__option">
          <input
            :checked="state.linkingInput.orderExcelInfoSaveMode === 'append'"
            type="radio"
            name="order-excel-info-save-mode"
            value="append"
            @change="updateOrderExcelInfoSaveMode('append')"
          />
          <span>{{ t('既存の設定に追加する') }}</span>
        </label>
        <label v-if="savedOrderExcelInfos && savedOrderExcelInfos.length > 0" class="order-save-mode__option">
          <input
            :checked="state.linkingInput.orderExcelInfoSaveMode === 'replace'"
            type="radio"
            name="order-excel-info-save-mode"
            value="replace"
            @change="updateOrderExcelInfoSaveMode('replace')"
          />
          <span>{{ t('既存の設定を置き換える') }}</span>
        </label>
        <select
          v-if="state.linkingInput.orderExcelInfoSaveMode === 'replace' && savedOrderExcelInfos.length > 0"
          :value="state.linkingInput.orderExcelInfoReplaceTargetId"
          class="order-save-mode__select"
          @change="updateOrderExcelInfoReplaceTargetId"
        >
          <option v-for="orderExcelInfo in savedOrderExcelInfos" :key="orderExcelInfo.id" :value="orderExcelInfo.id">
            {{ orderExcelInfo.name }}
          </option>
        </select>
      </div>
      <InputOrderExcelForm
        v-if="state.uploadInput.inputFileType === '発注'"
        from="LinkingInput"
        :selected-field-id="selectedFieldId"
        :suggestions="selectedFieldSuggestions"
        :order-excel-infos="state.linkingInput.orderExcelInfos"
        @update-selected-field-id="
          (id) => {
            selectedFieldId = id;
          }
        "
        @update-order-excel-infos="updateOrderExcelInfos"
      />
      <InputExcelForm
        v-else
        from="LinkingInput"
        :selected-field-id="selectedFieldId"
        :suggestions="selectedFieldSuggestions"
        :use-format-type="state.uploadInput.useFormatType"
        :alterable-header-infos="state.linkingInput.alternateHeaderInfos"
        :inlined-year-month-info="state.linkingInput.inlinedYearMonthInfo"
        :in-header-year-month-info="state.linkingInput.inHeaderYearMonthInfo"
        @update-selected-field-id="
          (id) => {
            selectedFieldId = id;
          }
        "
        @update-selected-field="updateSelectedField"
        @update-inline-header-name="updateInlineHeaderName"
        @update-in-header-start-column-number="updateInHeaderStartColumnNumber"
        @update-in-header-end-column-number="updateInHeaderEndColumnNumber"
        @update-in-header-start="updateInHeaderStart"
        @update-in-header-end="updateInHeaderEnd"
      />
    </div>

    <div class="linking-input__button-container">
      <TextButton
        :button-style="'btn-secondary'"
        :size="'btn-md'"
        :on-click="onClickBackButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：戻るボタン') }}
      </TextButton>
      <TextButton
        :button-style="'btn-primary'"
        :size="'btn-md'"
        :on-click="onClickNextButton"
        :disabled="false"
        class="bulk-action-button-container__export-button"
      >
        {{ t('初期設定：登録ボタン') }}
      </TextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.linking-input {
  display: grid;
  gap: 12px;

  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  border-radius: 4px;
  margin-top: 16px;
  padding: 16px;

  &__title {
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__progress {
    justify-self: center;
  }

  &__linking-container {
    display: grid;
    gap: 12px;
    margin: 12px 0 4px;
  }

  &__button-container {
    display: grid;
    justify-self: center;
    gap: 8px;
    grid-template-columns: repeat(2, fit-content(100%));
    margin-top: 0;
  }
}

.order-save-mode {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;

  &__option {
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $textDefault;
  }

  &__select {
    min-height: 32px;
    min-width: 220px;
    padding: 4px 8px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    font-family: Noto Sans JP;
    font-size: 14px;
    color: $textDefault;
    background-color: $semanticWhite;
  }

  &__empty {
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: $textSecondary;
  }
}

.sub-title {
  display: grid;
  gap: 16px;
  grid-template-rows: repeat(2, fit-content(100%));

  &__prefix {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: $textDefault;
  }

  &__suffix {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: $textDefault;
  }
}
</style>
